/**
 * 운영자의 대행사 화면 — 목록·상세(261025-agency-white-label). **읽기 전용**이다:
 * 대행사 등록·호스트·직원 계정·경계 이동은 그대로 운영자 CLI(`cli agency …`)가 한다(5곳 이하 — 손 온보딩).
 * ★ 운영자 전용(`access: 'admin'`) — 다른 대행사·호스트 목록을 비-admin 응답에 싣지 않는다
 */
import { z } from 'zod';
import { AGENCY_ROLES } from '../agency.ts';
import { BRAND_STATUSES } from '../brand-status.ts';
import { IdParams, IsoDateTime } from '../common.ts';
import { defineRoute } from '../route.ts';

export const AgencySummary = z.object({
  id: z.uuid(),
  name: z.string(),
  /** 고객에게 보이는 서비스 이름(브랜딩) */
  serviceName: z.string(),
  /** 연결된 호스트 — 먼저 등록한 것이 대표 호스트(링크 오리진)다. 비면 화이트라벨이 아니다 */
  hosts: z.array(z.string()),
  brandCount: z.number().int(),
  staffCount: z.number().int(),
  clientCount: z.number().int(),
  /** 초대 메일 발신 도메인 — DKIM 확인을 마친 것만. null 이면 대행사 고객 초대가 거부된다 */
  emailDomain: z.string().nullable(),
  /** 대행사 카카오 채널(발신 프로필)이 설정됐는가 */
  alimtalk: z.boolean(),
  /** 약관·처리방침 본문을 우리가 게시하는가(대행사 호스트의 /terms·/privacy) */
  legalDocs: z.boolean(),
  createdAt: IsoDateTime,
});
export type AgencySummary = z.infer<typeof AgencySummary>;

export const listAgencies = defineRoute({
  method: 'get',
  path: '/api/agencies',
  access: 'admin',
  summary: '대행사 목록 — 호스트·브랜드·계정 수·메일·알림톡 준비 상태',
  response: z.object({ agencies: z.array(AgencySummary) }),
});

export const AgencyAccount = z.object({
  id: z.uuid(),
  email: z.string(),
  name: z.string().nullable(),
  role: z.enum(AGENCY_ROLES),
  /** 살아 있는 브랜드 소속 수 */
  brandCount: z.number().int(),
  /** 첫 로그인 전(임시 비밀번호 그대로) */
  mustChangePassword: z.boolean(),
  /** 마지막 본인 로그인 — 대리 로그인은 세지 않는다 */
  lastLoginAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});

export const AgencyBrand = z.object({
  id: z.uuid(),
  name: z.string(),
  status: z.enum(BRAND_STATUSES),
  isTest: z.boolean(),
  createdAt: IsoDateTime,
});

export const getAgency = defineRoute({
  method: 'get',
  path: '/api/agencies/{id}',
  access: 'admin',
  summary: '대행사 상세 — 계정(직원·고객)과 브랜드',
  params: IdParams,
  response: z.object({
    agency: AgencySummary,
    accounts: z.array(AgencyAccount),
    brands: z.array(AgencyBrand),
  }),
});
